/**
 * Mirror of `backend/src/monitoring/monitoring.types.ts` (and the enums its
 * entities export). Keep the two files in step: the UI reads these shapes
 * straight off `/api/monitoring`.
 */

/* ------------------------------------------------------------------ */
/* Enums                                                               */
/* ------------------------------------------------------------------ */

export type WatchSubjectType = 'stakeholder' | 'organization';
export type WatchFrequency = 'daily' | 'weekly' | 'paused';

export type SignalCategory =
  | 'appointment'
  | 'departure'
  | 'legal'
  | 'regulatory'
  | 'protest'
  | 'financial'
  | 'corruption'
  | 'partnership'
  | 'statement'
  | 'security'
  | 'other';

export type SignalSeverity = 'low' | 'medium' | 'high' | 'critical';
export type SignalStatus = 'new' | 'reviewed' | 'dismissed' | 'escalated';

/** Why the system is raising its hand */
export type AlertKind =
  | 'sentiment_drop'
  | 'volume_spike'
  | 'high_severity'
  | 'role_change'
  | 'co_mention'
  | 'going_dark';

export type AlertStatus = 'open' | 'acknowledged' | 'resolved';

export interface SubjectRef {
  type: WatchSubjectType;
  id: string;
  label: string;
}

/* ------------------------------------------------------------------ */
/* Watches                                                             */
/* ------------------------------------------------------------------ */

export interface WatchView {
  id: string;
  subject: SubjectRef;
  frequency: WatchFrequency;
  keywords: string[];
  languages: string[];
  lastScanAt: string | null;
  nextScanAt: string | null;
  lastError: string | null;
  /** True while this watch is the one being scanned */
  scanning: boolean;
  signalCount: number;
  /** Signals in the last 30 days */
  recentSignals: number;
  updatedAt: string;
}

export interface CreateWatchInput {
  subjectType: WatchSubjectType;
  subjectId: string;
  frequency?: WatchFrequency;
  keywords?: string[];
  languages?: string[];
}

export interface UpdateWatchInput {
  frequency?: WatchFrequency;
  keywords?: string[];
  languages?: string[];
}

/* ------------------------------------------------------------------ */
/* Signals                                                             */
/* ------------------------------------------------------------------ */

export interface SignalView {
  id: string;
  subject: SubjectRef;
  title: string;
  url: string | null;
  source: string | null;
  publishedAt: string | null;
  summary: string;
  category: SignalCategory;
  /** -1 hostile … +1 favourable */
  sentiment: number;
  severity: SignalSeverity;
  confidence: number;
  status: SignalStatus;
  mentions: SubjectRef[];
  detectedAt: string;
}

export interface SignalQuery {
  subjectType?: WatchSubjectType;
  subjectId?: string;
  status?: SignalStatus | 'open';
  category?: SignalCategory;
  severity?: SignalSeverity;
  /** ISO date: only signals detected at or after this moment */
  since?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface SignalPage {
  items: SignalView[];
  nextCursor: string | null;
  total: number;
}

/* ------------------------------------------------------------------ */
/* Scanning                                                            */
/* ------------------------------------------------------------------ */

export interface ScanResult {
  subject: SubjectRef;
  found: number;
  /** New after de-duplication against what is already stored */
  stored: number;
  duplicates: number;
  rejected: number;
  alertsRaised: number;
  error?: string;
  durationMs: number;
}

export interface ScanRunSummary {
  startedAt: string;
  finishedAt: string;
  scanned: number;
  stored: number;
  alertsRaised: number;
  failures: number;
  results: ScanResult[];
  /** Set when nothing ran, e.g. no research provider configured */
  skippedReason?: string;
}

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

export interface AlertView {
  id: string;
  kind: AlertKind;
  subject: SubjectRef | null;
  title: string;
  explanation: string;
  severity: SignalSeverity;
  status: AlertStatus;
  evidence: {
    signalIds?: string[];
    metrics?: Record<string, number>;
    related?: SubjectRef[];
  };
  /** Signals the alert was raised from, for reading in place */
  signals?: SignalView[];
  windowStart: string | null;
  windowEnd: string | null;
  createdAt: string;
}

export interface AlertQuery {
  status?: AlertStatus;
  /** Attach the signals each alert was raised from */
  includeSignals?: boolean;
  kind?: AlertKind;
  severity?: SignalSeverity;
  subjectId?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

/** Thresholds the rules use, adjustable by an admin */
export interface MonitoringThresholds {
  /** Mean sentiment must fall by at least this much to raise sentiment_drop */
  sentimentDrop: number;
  /** Recent volume must exceed the baseline by this multiple */
  volumeSpikeFactor: number;
  /** Signals needed in a window before a rule may fire */
  minSignals: number;
  /** Days without a signal before an influential stakeholder counts as dark */
  goingDarkDays: number;
  /** Ceiling on entities scanned per scheduled run, to bound AI cost */
  maxScansPerRun: number;
}

export const DEFAULT_THRESHOLDS: MonitoringThresholds = {
  sentimentDrop: 0.35,
  volumeSpikeFactor: 2,
  minSignals: 3,
  goingDarkDays: 90,
  maxScansPerRun: 25,
};

/* ------------------------------------------------------------------ */
/* Digest                                                              */
/* ------------------------------------------------------------------ */

export interface DigestRequest {
  /** Days covered by the brief */
  days?: number;
  subjectType?: WatchSubjectType;
  subjectId?: string;
}

/* ------------------------------------------------------------------ */
/* Overview                                                            */
/* ------------------------------------------------------------------ */

/**
 * What the scanner is doing right now. Scans run one at a time to bound AI
 * spend, so the UI says so rather than letting a click be refused.
 */
export interface ScanState {
  running: boolean;
  startedAt: string | null;
  scope: 'watch' | 'due' | null;
  watchId: string | null;
  subject: SubjectRef | null;
  done: number;
  total: number | null;
}

export interface MonitoringOverview {
  watches: { total: number; daily: number; weekly: number; paused: number };
  signals: { last7Days: number; new: number; bySeverity: Record<SignalSeverity, number> };
  alerts: { open: number; bySeverity: Record<SignalSeverity, number> };
  lastScanAt: string | null;
  nextScanAt: string | null;
  /** False when no AI provider has research enabled — the UI says so plainly */
  researchReady: boolean;
  scan: ScanState;
}
